'use client';

import { useState } from 'react';
import { Box, Typography, Grid, Breadcrumbs, Link, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CategoryCard from '../components/CategoryCard';
import { categoryConfig, CategoryNode } from '../CategoryConfig';

interface CategoryStepProps {
  discipline: string;
  selectedCategories: string[];
  onSelectCategory: (categoryId: string) => void;
}

export default function CategoryStep({
  discipline,
  selectedCategories,
  onSelectCategory,
}: CategoryStepProps) {
  const [categoryPath, setCategoryPath] = useState<CategoryNode[]>([]);

  const currentCategories =
    categoryPath.length === 0
      ? categoryConfig[discipline as keyof typeof categoryConfig]
      : categoryPath[categoryPath.length - 1].children || [];

  const handleCategoryClick = (category: CategoryNode) => {
    if (category.children) {
      setCategoryPath(prev => [...prev, category]);
    } else {
      onSelectCategory(category.id);
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    setCategoryPath(prev => prev.slice(0, index));
  };

  const handleBackClick = () => {
    setCategoryPath(prev => prev.slice(0, -1));
  };

  return (
    <div>
      <Typography variant="h5" gutterBottom>
        Select Categories
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {discipline === 'design'
          ? 'Choose the type of design work you need'
          : 'Select the manufacturing processes and materials you require'}
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {categoryPath.length > 0 && (
            <IconButton onClick={handleBackClick} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Breadcrumbs>
            <Link
              component="button"
              variant="body1"
              onClick={() => handleBreadcrumbClick(0)}
              underline="hover"
              color="inherit"
              sx={{ cursor: 'pointer' }}
              {...({} as any)}
            >
              {discipline === 'design' ? 'Design Categories' : 'Manufacturing Processes'}
            </Link>
            {categoryPath.map((category, index) => (
              <Link
                key={category.id}
                component="button"
                variant="body1"
                onClick={() => handleBreadcrumbClick(index + 1)}
                underline="hover"
                color="inherit"
                sx={{ cursor: 'pointer' }}
                {...({} as any)}
              >
                {category.label}
              </Link>
            ))}
          </Breadcrumbs>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {currentCategories.map(category => (
          <Grid item xs={12} sm={6} md={4} key={category.id}>
            <CategoryCard
              id={category.id}
              label={category.label}
              description={category.description}
              selected={selectedCategories.includes(category.id)}
              onClick={() => handleCategoryClick(category)}
              multiSelect={!category.children}
            />
          </Grid>
        ))}
      </Grid>

      {selectedCategories.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" gutterBottom>
            Selected Categories:
          </Typography>
          <Grid container spacing={1}>
            {selectedCategories.map(categoryId => (
              <Grid item key={categoryId}>
                <Typography variant="body2" color="text.secondary">
                  • {categoryId}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </div>
  );
}
